/**
 * Date and time helpers, ported from legacy `src/utils/functions.ts`.
 */

/**
 * Left-pads a clock component to two digits: `7` -> `'07'`, `'7'` -> `'07'`.
 *
 * The legacy version branched on `typeof` and ended with
 * `throw new Error('Unsupported type' + typeof time)` for a case its own signature
 * (`string | number`) made unreachable. Dropped: `String(value)` handles both inputs
 * identically, so the impossible branch cannot be constructed and a display can never
 * crash inside a clock format.
 */
export function timePadding(value: string | number): string {
  return String(value).padStart(2, '0');
}

/**
 * The localised date and time `DateWidget` renders, as `[date, time]`.
 *
 * **The order is date-first while the widget renders time-first.** That is the legacy
 * signature and the legacy destructuring (`const [newDate, newTime] = getDateAndTime()`) is
 * correct; keep the two straight when calling it.
 *
 * Formats are the legacy ones exactly — time `{hour, minute, hour12: false}`, date
 * `{month: 'short', day: 'numeric', weekday: 'long', year: 'numeric'}` — so `en-GB` yields
 * `'14:05'` and `'Tuesday, 5 Aug 2026'`.
 *
 * `now` is a parameter purely so tests can pin an instant; every caller omits it.
 */
export function getDateAndTime(now: Date = new Date()): [date: string, time: string] {
  const locale = documentLocale();

  const time = now.toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  });

  const date = now.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    weekday: 'long',
    year: 'numeric',
  });

  return [date, time];
}

/**
 * `<html lang>` — written from `config.locale` by `main.tsx` before the first render and
 * owned by `useDocumentTheme` afterwards (SYM-502), so it is the config's locale by the time
 * any widget reads it.
 *
 * The browser-language fallbacks are the legacy ones. They are unreachable in this app (the
 * attribute is always set, and `locale` defaults to `en-GB` in the schema) but a signage
 * display that fell back to the *browser* locale would render dates in whatever language the
 * kiosk image happened to ship with, so the chain is kept rather than replaced by a hardcoded
 * default that would hide the misconfiguration.
 */
function documentLocale(): string {
  const lang = document.documentElement.getAttribute('lang');
  if (lang) return lang;
  return navigator.languages[0] ?? navigator.language;
}
